import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

export interface LicenseFile {
  fileId: string;
  /** Monotonic per customer; a lower serial cannot overwrite a license granted by a higher one. */
  fileSerial: number;
  issuer: string;
  issuedAt: string;
  installationId: string;
  fingerprint: string;
  signatureDigest: string;
  customer: { organizationName: string, contact?: string };
  licenseIds: string[];
  importedAt: string;
}

export interface ImportResult {
  fileId: string;
  imported: string[];
  updated: string[];
  /** Licenses a newer file already grants; this file's older entitlement was not applied. */
  superseded: string[];
  /** Licenses whose own offline credit values were above this file's and were lowered to it. */
  creditLowered: string[];
  /** Licenses whose own seat time was shorter than this file's and was raised to it. */
  seatTimeRaised: string[];
  /** The file re-bound an installation whose host had changed. */
  rebound: boolean;
}

export const licenseFileApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getLicenseFiles: builder.query<LicenseFile[], void>({
      query: () => '/v1/license-files',
      providesTags: ['LicenseFile'],
      onQueryStarted: handleApiResult(),
    }),
    importLicenseFile: builder.mutation<ImportResult, File>({
      query: (file) => {
        const body = new FormData();
        body.append('file', file);

        // No Content-Type header: the browser must set the multipart boundary itself.
        return { url: '/v1/license-files', method: 'POST', body };
      },
      invalidatesTags: ['LicenseFile', 'License', 'Installation'],
    }),
    removeLicenseFile: builder.mutation<void, string>({
      query: (fileId) => ({ url: `/v1/license-files/${fileId}`, method: 'DELETE' }),
      invalidatesTags: ['LicenseFile', 'License', 'Installation'],
      onQueryStarted: handleApiResult('License file removed.'),
    }),
  }),
});

export const {
  useGetLicenseFilesQuery,
  useImportLicenseFileMutation,
  useRemoveLicenseFileMutation,
} = licenseFileApiSlice;
